import {ApolloLink, Observable} from 'apollo-link'

import {readApiDomain} from './runtime-api'
import {readBearerToken} from './token'

/**
 * Apollo v1 link that authorizes every operation the VN way: an explicit `Authorization: Bearer`
 * header (the token must reach whatever rotating domain is live now — a cookie set on a previous
 * domain would not) and `credentials: 'omit'` so no origin-bound cookie is ever attached. The token
 * read is async (IndexedDB), so we wrap forwarding in an Observable that resolves it first.
 *
 * The bearer is attached only when a runtime host is resolved: with no domain the operation falls
 * back to the baked build host, and the token must not ride along to it (PT-5648).
 *
 * `readToken` and `hasRuntimeDomain` are injected for tests; production reads the shared IndexedDB
 * store and the persisted runtime domain.
 */
export const createVnAuthLink = (
  readToken: () => Promise<string | null> = readBearerToken,
  hasRuntimeDomain: () => boolean = () => readApiDomain() !== null,
): ApolloLink =>
  new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        let sub: {unsubscribe: () => void} | undefined
        readToken().then((token) => {
          const attachToken = token !== null && hasRuntimeDomain()
          operation.setContext((prev: {headers?: Record<string, string>}) => ({
            ...prev,
            credentials: 'omit',
            headers: attachToken ? {...(prev.headers ?? {}), Authorization: `Bearer ${token}`} : prev.headers ?? {},
          }))
          sub = forward(operation).subscribe(observer)
        })
        return (): void => sub?.unsubscribe()
      }),
  )
